import {ActivatedRouteSnapshot, CanActivateChildFn, CanActivateFn, ParamMap, Route, Router} from '@angular/router';
import {landingPageRoute} from "./pages/landing-page/landing-page.route";
import {CallbackComponent} from "./pages/callback/callback.component";
import {SigninPageComponent} from "./pages/signup-section/signin-page/signin-page.component";
import {ForgotPasswordPageComponent} from "./pages/signup-section/forgot-password-page/forgot-password-page.component";
import {ResetPasswordPageComponent} from "./pages/signup-section/reset-password-page/reset-password-page.component";
import {SitemapComponent} from "./pages/sitemap/sitemap.component";
import {extraPages} from "./pages/extra-pages/extra-pages.routes";
import {NotFoundPageComponent} from "./pages/not-found-page/not-found-page.component";
import {accountSectionRoute} from "./pages/account-section/account-section.routes";
import {TabsContainerComponent} from "./pages/tabs-container/tabs-container.component";
import {gotoHomepageIfLoggedInGuard, postSignupFlowGuard,} from "../router.helper";
import {firstValueFrom, Observable} from "rxjs";
import {inject, PLATFORM_ID} from "@angular/core";
import {isPlatformBrowser} from "@angular/common";
import {AnalyticsService} from "./services/analytics.service";
import {retryImport} from "./utils";
import {
  WaitingForFederatedAuthComponent
} from "./pages/waiting-for-federated-auth/waiting-for-federated-auth.component";
import {paymentSuccessRoute} from "./pages/signup-section/signup-route-values";
import {EmptyComponent} from "./pages/empty/empty.component";
import {UserService} from "./services/user.service";
import {
  DeferredPaymentSuccessPageComponent
} from "./pages/payment/deferred-payment-success-page/deferred-payment-success-page.component";
import {
  DeferredNoTrialSuccessPageComponent
} from "./pages/payment/deferred-nt-success-page/deferred-nt-success-page.component";
import {
  ResubscribePaymentSuccessPageComponent
} from "./pages/payment/resubscribe-payment-success-page/resubscribe-payment-success-page.component";
import {ReviewPageComponent} from "./pages/review/review-page.component";
import {
  PublicNotificationSettingsPageComponent
} from "./pages/public-notification-settings/public-notification-settings-page.component";
import {
  ResubscribeRedirectPageComponent
} from "./pages/resubscribe-redirect/resubscribe-redirect-page.component";
import {
  PayRedirectPageComponent
} from "./pages/pay-redirect/pay-redirect-page.component";
import {WikiPageComponent} from "./pages/wiki-page/wiki-page.component";

export const defaultTitle = 'Homey NYC - Housing Lottery Made Easy';
export const defaultDescription = 'The Homey app simplifies applying to NYC Housing Lottery apartments. Enter every eligible affordable housing lottery, for low income housing and medium income housing.';

export type HeaderStyle =
  'landing-sub-page'
  | 'no-back-sub-page'
  | 'sub-page'
  | 'sub-page-with-logo'

  | 'root'
  | 'landing'
  | 'hidden';

export interface RouteData {
  metaDescription?: string | ((activatedRoute: ActivatedRouteSnapshot) => string | Observable<string>),
  headerStyle?: HeaderStyle;
  tab?: 'profile' | 'lotteries' | 'applications' | 'support';
  signupToApplyRoute?: string[];
  signupToApplyText?: string;
  signupToApplyRouteParams?: (params: ParamMap) => Observable<object>
  backRoute?: string[] | (() => string[]);
  index?: boolean;
  canonicalUrl?: (params: ParamMap) => string | Observable<string>;
  structuredData?: (params: ParamMap) => Observable<object> | undefined;
  showCarouselEligibilityCta?: boolean;
  showPromotionHeader?: boolean;
  hideWarningHeader?: boolean;
  introBgClass?: string;
  heroImage?: string;
  logosImage?: string;
  badgeImage?: string;
  starsImage?: string;
  backgroundType?: 'default' | 'blue-gradient';
}

export interface HomeyRoute extends Route {
  data?: RouteData;
  children?: HomeyRoute[];
}

export const stripQueryParamsGuard: CanActivateChildFn = (route, state) => {
  const platformId = inject(PLATFORM_ID);

  // Skip on server so s/v params survive into client hydration
  if (!isPlatformBrowser(platformId)) return true;

  const router = inject(Router);
  const analytics = inject(AnalyticsService);

  // Parse the full current URL
  const tree = router.parseUrl(state.url);
  const params = tree.queryParamMap;

  let changed = false;
  let sourceValue: string | undefined;
  let variantValue: string | undefined;
  const next: Record<string, string[] | string | null> = {};

  for (const key of params.keys) {
    if (key === 's') {
      changed = true;
      sourceValue = params.get(key) ?? undefined;
      continue;
    }

    if (key === 'v') {
      changed = true;
      variantValue = params.get(key) ?? undefined;
      continue;
    }

    next[key] = params.getAll(key).length > 1 ? params.getAll(key) : params.get(key);
  }

  if (sourceValue) {
    analytics.setClickSource(sourceValue, tree.toString().split('?')[0], variantValue);
  }

  if (!changed) return true;

  // ✅ mutate the same UrlTree safely
  tree.queryParams = next;
  return tree;
};

const paymentCanceledRedirectGuard: CanActivateFn = async () => {
  const router = inject(Router);
  const userService = inject(UserService);

  const user = await firstValueFrom(userService.user$);

  if (user.status === 'mandatory-flow' && user.paymentInfo.type === 'deferred') {
    return router.createUrlTree(['/profile', 'update', 'income-assets-info', 'complete']);
  }

  return router.createUrlTree(['/signup', 'benefits'], {
    queryParams: {payment: 'cancelled'},
  });
};

export const loginRoute = ['/login']

export const routes: HomeyRoute[] = [
  landingPageRoute,
  {
    path: '',
    data: {headerStyle: 'landing-sub-page'},
    children: extraPages
  },
  {
    path: 'callback',
    component: CallbackComponent,
    data: {headerStyle: 'no-back-sub-page'}
  },
  {
    path: 'federated-login',
    component: WaitingForFederatedAuthComponent,
    data: {headerStyle: 'no-back-sub-page'},
  },
  {
    path: 'payment',
    data: {headerStyle: 'no-back-sub-page'},
    children: [
      {
        path: 'success',
        component: EmptyComponent,
        canActivate: [async () => {
          const router = inject(Router);
          const analyticsService = inject(AnalyticsService);
          const intervalParam = router.routerState.snapshot.root.queryParams['interval'];
          const interval = intervalParam === 'annual' ? 'annual' : intervalParam === 'monthly' ? 'monthly' : 'unknown';
          await analyticsService.sendPaymentSuccess({
            flow: 'initial',
            checkoutLocation: 'default',
            interval,
            amount: 15,
          });
          return router.createUrlTree(paymentSuccessRoute);
        }]
      },
      {
        path: 'deferred-success',
        component: DeferredPaymentSuccessPageComponent,
      },
      {
        path: 'deferred-nt-success',
        component: DeferredNoTrialSuccessPageComponent,
      },
      {
        path: 'resubscribe-success',
        component: ResubscribePaymentSuccessPageComponent,
      },
      {
        path: 'canceled',
        component: EmptyComponent,
        canActivate: [paymentCanceledRedirectGuard],
      },
    ]
  },
  {path: 'logout', redirectTo: ''},
  {
    path: '',
    component: TabsContainerComponent,
    data: {headerStyle: 'no-back-sub-page'},
    canActivateChild: [postSignupFlowGuard],
    loadChildren: retryImport(() => import('./tabs-routes').then(m => m.tabsRoutes))
  },
  {
    path: 'lotteries',
    canActivate: [postSignupFlowGuard],
    loadChildren: retryImport(() => import('./pages/apartments-tab/lotteries.routes').then(m => m.lotteriesTabChildRoutes)),
  },
  {
    path: 'review',
    component: ReviewPageComponent,
    data: {headerStyle: 'no-back-sub-page'},
  },
  {
    path: 'resubscribe',
    component: ResubscribeRedirectPageComponent,
    data: {headerStyle: 'no-back-sub-page', hideWarningHeader: true},
  },
  {
    path: 'pay',
    component: PayRedirectPageComponent,
    data: {headerStyle: 'no-back-sub-page', hideWarningHeader: true},
  },
  {
    path: 'notification-settings',
    component: PublicNotificationSettingsPageComponent,
    data: {headerStyle: 'sub-page-with-logo'},
  },
  {
    path: 'profile',
    canActivateChild: [postSignupFlowGuard],
    loadChildren: retryImport(() => import('./pages/profile-tab/profile-tab.routes').then(m => m.profileTabChildRoutes)),
  },
  {
    path: 'applications',
    canActivateChild: [postSignupFlowGuard],
    loadChildren: retryImport(() => import('./pages/applications-tab/application-tab.routes').then(m => m.applicationTabChildRoutes)),
  },
  accountSectionRoute,
  {
    path: 'login', children:
      [
        {
          path: '',
          pathMatch: 'full',
          canActivate: [gotoHomepageIfLoggedInGuard],
          component: SigninPageComponent,
          data: {index: true, headerStyle: 'sub-page-with-logo'}
        },
        {path: 'forgot-password', component: ForgotPasswordPageComponent},
        {path: 'reset-password', component: ResetPasswordPageComponent, data: {headerStyle: 'no-back-sub-page'}},
      ],
  },
  {
    path: 'wiki/:slug',
    component: WikiPageComponent,
    data: {headerStyle: 'sub-page-with-logo'},
  },
  {path: 'generate-sitemap', component: SitemapComponent},
  {
    path: '**', component: NotFoundPageComponent, data: {
      headerStyle: "sub-page-with-logo"
    }
  }
];
